import { conditionToDsl } from '@platform/core/lib/filterDsl';
import type { DesignSectionKey } from './DesignSectionKey';
import type { IDesignSub } from './IDesignSub';

/** Модуль бізнес-аналізу, записи якого описують кожен модуль (прототип v4 baFor: records.ba, поле module — назва модуля англійською). */
export const analysisModuleKey = 'ba';
/** Поля запису бізнес-аналізу: вид, модуль, MoSCoW; номер запису (наскрізні шляхи модуля — множиною номерів, journeyCodesOf). */
export const kindField = 'kind';
export const moduleField = 'module';
export const moscowField = 'moscow';
const CODE = 'code';
/** Номер, якого не має жоден запис: порожня множина шляхів дає порожній список, а не всі шляхи. */
const NO_CODE = '0';

/** Розділи «Проєктування» в порядку меню прототипу v4. */
export const designSectionKeys: readonly DesignSectionKey[] = ['features', 'motivation', 'stakeholders', 'journeys', 'useCases', 'requirements', 'model'];

const sub = (key: string, kinds: string[], scope: IDesignSub['scope'] = 'module', moscow?: string): IDesignSub => ({ key, kinds, scope, moscow });

/** Вкладки-пігулки розділів-списків (прототип v4 DSG_SUBS / SUBS); перша — типова. Модель даних — не список. */
const SUBS: Record<Exclude<DesignSectionKey, 'model'>, IDesignSub[]> = {
  features: [sub('all', ['Feature'])],
  motivation: [sub('all', ['Problem', 'Risk', 'Opportunity']), sub('problems', ['Problem']), sub('risks', ['Risk']), sub('opportunities', ['Opportunity'])],
  stakeholders: [sub('stakeholders', ['Stakeholder'], 'any'), sub('needs', ['Stakeholder need'])],
  journeys: [sub('all', ['Customer journey'], 'journey')],
  useCases: [sub('all', ['Use case'])],
  requirements: [sub('all', ['Requirement']), sub('must', ['Requirement'], 'module', 'Must'), sub('should', ['Requirement'], 'module', 'Should'), sub('could', ['Requirement'], 'module', 'Could')],
};

/** Чи ключ — розділ «Проєктування». */
export const isDesignSection = (key: string | undefined): key is DesignSectionKey => designSectionKeys.includes(key as DesignSectionKey);

/** Вкладки розділу-списку (порожньо для моделі даних). */
export const designSubsOf = (section: DesignSectionKey): IDesignSub[] => (section === 'model' ? [] : SUBS[section]);

/** Вкладка за ключем, інакше перша. */
export function designSubOf(section: DesignSectionKey, key: string | null | undefined): IDesignSub | undefined {
  const subs = designSubsOf(section);
  return subs.find((s) => s.key === key) ?? subs[0];
}

/**
 * Фільтр-DSL записів бізнес-аналізу вкладки (прототип v4 dsgKey): вид (один чи кілька), модуль за назвою англійською (`moduleName`, як
 * MOD_BASE_NAME) або — для наскрізних шляхів — номери шляхів модуля (`journeyCodes`, journeyCodesOf: правило прототипу з урахуванням
 * регістру, якого фільтр API не має), MoSCoW вимог. Чиста функція.
 */
export function designFilterOf(item: IDesignSub, moduleName: string, journeyCodes: readonly number[] = []): string {
  const parts = [conditionToDsl({ field: kindField, op: item.kinds.length > 1 ? 'in' : 'eq', values: item.kinds })];
  if (item.scope === 'module') parts.push(conditionToDsl({ field: moduleField, op: 'eq', values: [moduleName] }));
  if (item.scope === 'journey') parts.push(conditionToDsl({ field: CODE, op: 'in', values: journeyCodes.length ? journeyCodes.map(String) : [NO_CODE] }));
  if (item.moscow) parts.push(conditionToDsl({ field: moscowField, op: 'eq', values: [item.moscow] }));
  return parts.join(';');
}

/** Початкові значення нового запису вкладки: вид (перший), модуль (крім переліку сторін платформи) і MoSCoW (прототип v4 createRecord). */
export function designPresetOf(item: IDesignSub, moduleName: string): Record<string, string> {
  return {
    [kindField]: item.kinds[0] ?? '',
    ...(item.scope === 'any' ? {} : { [moduleField]: moduleName }),
    ...(item.moscow ? { [moscowField]: item.moscow } : {}),
  };
}
